import { Injectable, signal, computed, OnDestroy } from '@angular/core';

/**
 * Rate limit information from HTTP response headers
 */
export interface RateLimitInfo {
	/** Maximum requests allowed in the time window */
	limit: number;
	/** Remaining requests in current window */
	remaining: number;
	/** Unix timestamp when the rate limit resets */
	reset: number;
	/** Seconds to wait before retrying (from Retry-After header) */
	retryAfter?: number;
	/** Endpoint this rate limit applies to */
	endpoint?: string;
	/** When this info was last updated */
	updatedAt: number;
}

/**
 * Service for managing rate limit state across the application
 * Parses X-RateLimit-* headers and provides reactive signals
 */
@Injectable({
	providedIn: 'root',
})
export class RateLimitService implements OnDestroy {
	// Map of endpoint -> rate limit info
	private readonly _rateLimits = signal<Map<string, RateLimitInfo>>(new Map());

	// Global rate limit state (for current endpoint)
	private readonly _currentRateLimit = signal<RateLimitInfo | null>(null);

	private countdownInterval: ReturnType<typeof setInterval> | null = null;

	// Computed signals
	readonly currentRateLimit = computed(() => this._currentRateLimit());
	readonly isRateLimited = computed(() => {
		const current = this._currentRateLimit();
		return current ? current.remaining === 0 : false;
	});
	readonly timeUntilReset = computed(() => {
		const current = this._currentRateLimit();

		if (!current) {
			return 0;
		}

		const now = Math.floor(Date.now() / 1000);
		return Math.max(0, current.reset - now);
	});

	constructor() {
		// Start countdown interval to update timeUntilReset
		this.countdownInterval = setInterval(() => {
			// Force recomputation by setting the same value
			const current = this._currentRateLimit();

			if (current) {
				this._currentRateLimit.set({ ...current });
			}
		}, 1000);
	}

	ngOnDestroy(): void {
		if (this.countdownInterval) {
			clearInterval(this.countdownInterval);
			this.countdownInterval = null;
		}
	}

	/**
	 * Update rate limit info from HTTP response headers
	 */
	updateFromHeaders(
		headers: {
			'x-ratelimit-limit'?: string | null;
			'x-ratelimit-remaining'?: string | null;
			'x-ratelimit-reset'?: string | null;
			'retry-after'?: string | null;
		},
		endpoint?: string,
	): void {
		const limit = headers['x-ratelimit-limit'];
		const remaining = headers['x-ratelimit-remaining'];
		const reset = headers['x-ratelimit-reset'];
		const retryAfter = headers['retry-after'];

		if (!limit || !remaining || !reset) {
			return; // Not a rate-limited response
		}

		const info: RateLimitInfo = {
			limit: parseInt(limit, 10),
			remaining: parseInt(remaining, 10),
			reset: parseInt(reset, 10),
			retryAfter: retryAfter ? parseInt(retryAfter, 10) : undefined,
			endpoint,
			updatedAt: Math.floor(Date.now() / 1000),
		};

		// Update map
		if (endpoint) {
			const map = new Map(this._rateLimits());
			map.set(endpoint, info);
			this._rateLimits.set(map);
		}

		// Update current
		this._currentRateLimit.set(info);
	}

	/**
	 * Get rate limit info for specific endpoint
	 */
	getRateLimitForEndpoint(endpoint: string): RateLimitInfo | undefined {
		return this._rateLimits().get(endpoint);
	}

	/**
	 * Check if an endpoint is currently rate limited
	 */
	isEndpointRateLimited(endpoint: string): boolean {
		const info = this.getRateLimitForEndpoint(endpoint);

		if (!info) {
			return false;
		}

		const now = Math.floor(Date.now() / 1000);
		return info.remaining === 0 && info.reset > now;
	}

	/**
	 * Get seconds until rate limit resets for specific endpoint
	 */
	getTimeUntilReset(endpoint: string): number {
		const info = this.getRateLimitForEndpoint(endpoint);

		if (!info) {
			return 0;
		}

		const now = Math.floor(Date.now() / 1000);
		return Math.max(0, info.reset - now);
	}

	/**
	 * Clear rate limit info (e.g., on logout)
	 */
	clear(): void {
		this._rateLimits.set(new Map());
		this._currentRateLimit.set(null);
	}

	/**
	 * Clear rate limit info for specific endpoint
	 */
	clearEndpoint(endpoint: string): void {
		const map = new Map(this._rateLimits());
		map.delete(endpoint);
		this._rateLimits.set(map);

		// Clear current if it matches
		const current = this._currentRateLimit();

		if (current?.endpoint === endpoint) {
			this._currentRateLimit.set(null);
		}
	}
}
